import { PopoverClose } from "@radix-ui/react-popover"
import { useCallback, useEffect, useReducer, useState, useMemo, Fragment } from "react"
import { LoaderCircleIcon, TrashIcon, PlusIcon, PencilIcon, ChevronDownIcon, CheckIcon } from "lucide-react"
import { useFetcher, useRouteLoaderData, Outlet } from "react-router"
import type { ActionFunctionArgs } from "react-router"
import qs from "qs"
import { z } from "zod"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse } from "@/utils"

import type { RootLoader } from "@/routes/root"

const routeId = "brand-rules"
const sessionKey = "brand-rules-filter-storage"

const CRITERIA_TYPE = ["evidenceCount", "tag"] as const
const LOGICAL_JOIN = ["none", "and", "or"] as const
const RULE_LEVEL_TYPE = ["filter", "logical"] as const
const OUTPUT_TYPE = ["tag", "metadata"] as const
const RULE_SCOPE = ["case", "instance", "record"] as const
const OPERATOR = ["=", "!=", ">", ">="] as const

const TAG_OPERATORS = ["=", "!="] as const satisfies ReadonlyArray<(typeof OPERATOR)[number]>

function operatorsFor(criteriaType: (typeof CRITERIA_TYPE)[number]): ReadonlyArray<(typeof OPERATOR)[number]> {
  return criteriaType === "tag" ? TAG_OPERATORS : OPERATOR
}

const OPERATOR_LABELS: Record<(typeof OPERATOR)[number], string> = {
  "=": "=",
  "!=": "≠",
  ">": ">",
  ">=": "≥",
}

const LOGICAL_JOIN_LABELS: Record<(typeof LOGICAL_JOIN)[number], string> = {
  none: "None",
  and: "And",
  or: "Or",
}

const RULE_LEVEL_TYPE_LABELS: Record<(typeof RULE_LEVEL_TYPE)[number], string> = {
  filter: "Filter",
  logical: "Logical",
}

const OUTPUT_TYPE_LABELS: Record<(typeof OUTPUT_TYPE)[number], string> = {
  tag: "Tag",
  metadata: "Metadata",
}

const CRITERIA_TYPE_LABELS: Record<(typeof CRITERIA_TYPE)[number], string> = {
  evidenceCount: "Evidence Count",
  tag: "Tag",
}

const RULE_SCOPE_LABELS: Record<(typeof RULE_SCOPE)[number], string> = {
  case: "Case",
  instance: "Instance",
  record: "Record",
}

interface BrandRuleLevelCondition {
  criteriaLabel: string
  criteriaType: (typeof CRITERIA_TYPE)[number]
  criteriaValue: string
  operator: (typeof OPERATOR)[number]
  precedingLogicalJoin: (typeof LOGICAL_JOIN)[number]
}

interface BrandRuleLevel {
  level: number
  precedingLogicalJoin: (typeof LOGICAL_JOIN)[number]
  ruleLevelType: (typeof RULE_LEVEL_TYPE)[number]
  brandRuleLevelConditions: BrandRuleLevelCondition[]
}

interface BrandRuleOutput {
  type: (typeof OUTPUT_TYPE)[number]
  property: string | null
  tag: string | null
  tagType: string | null
  value: string | null
}

interface BrandRule {
  brand: string
  name: string
  isDeleted: boolean
  precedence: number
  ruleScope: (typeof RULE_SCOPE)[number]
  output: BrandRuleOutput
  brandRuleLevels: BrandRuleLevel[]
}

interface BrandTag {
  brand: string
  caseReference: string
  tagType: string
  name: string
  tagRestriction: string
}
interface EvidenceCountRecord {
  brand: string
  ruleScope: string
  name: string
  criteriaLabel: string
  criteriaType: string
  isDeleted: boolean
}

function emptyCondition(): BrandRuleLevelCondition {
  return { criteriaLabel: "", criteriaType: "tag", criteriaValue: "", operator: "=", precedingLogicalJoin: "none" }
}

function emptyLevel(level: number): BrandRuleLevel {
  return { level, precedingLogicalJoin: "none", ruleLevelType: "filter", brandRuleLevelConditions: [emptyCondition()] }
}

function useRulesData() {
  return useRouteLoaderData<typeof loader>(routeId)
}

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation =
    filters.parsed?.organisation == null || !organisations.includes(filters.parsed.organisation)
      ? organisations[0]
      : filters.parsed.organisation

  const projects = getProjectsByOrganisation(loadedOrganisation)
  const loadedProject =
    filters.parsed?.project == null || !projects.includes(filters.parsed.project) ? projects[0] : filters.parsed.project

  const encodedOrganisation = encodeURIComponent(loadedOrganisation)
  const encodedProject = encodeURIComponent(loadedProject)

  const [rules, tags, evidenceCounts] = await Promise.all([
    fetchAPI<BrandRule[]>(`/api/UnityViewer/Rule/${encodedOrganisation}/${encodedProject}`)
      .then(handleResponse)
      .then((rules: BrandRule[]) => rules.filter((r) => !r.isDeleted))
      .catch(() => []),
    fetchAPI<BrandTag[]>(`/api/UnityViewer/Tag/${encodedOrganisation}/${encodedProject}`)
      .then(handleResponse)
      .catch(() => []),
    fetchAPI<EvidenceCountRecord[]>(`/api/UnityViewer/EvidenceCount/${encodedOrganisation}/${encodedProject}`)
      .then(handleResponse)
      .then((records: EvidenceCountRecord[]) => records.filter((r) => !r.isDeleted))
      .catch(() => []),
  ])

  const result = {
    loadedOrganisation,
    projects,
    loadedProject,
    rules,
    tags,
    evidenceCounts,
  }

  return result
}

interface ApiValidationFailure {
  errorMessage?: string
}

interface ApiProblemDetails {
  detail?: string
  title?: string
}

interface JsonReadableResponse {
  json: () => Promise<unknown>
}

async function extractErrorMessage(response: JsonReadableResponse, fallback: string): Promise<string> {
  try {
    const body: unknown = await response.json()

    if (Array.isArray(body)) {
      const messages = (body as ApiValidationFailure[])
        .map((item) => item.errorMessage?.trim())
        .filter((message): message is string => Boolean(message))
      if (messages.length > 0) return messages.join(" ")
    } else if (body != null && typeof body === "object") {
      const { detail, title } = body as ApiProblemDetails
      if (typeof detail === "string" && detail.trim().length > 0) return detail
      if (typeof title === "string" && title.trim().length > 0) return title
    }
  } catch (error) {
    console.error("extractErrorMessage: failed to parse error response body", error)
  }

  return fallback
}

function firstZodMessage(error: z.ZodError, fallback: string): string {
  return error.issues[0]?.message ?? fallback
}

const nonEmptyString = (message: string) => z.string().trim().min(1, message)

const brandRuleLevelConditionSchema = z
  .object({
    criteriaLabel: z.string(),
    criteriaType: z.enum([...CRITERIA_TYPE]),
    criteriaValue: z.string(),
    operator: z.enum([...OPERATOR]),
    precedingLogicalJoin: z.enum([...LOGICAL_JOIN]),
  })
  .superRefine((condition, ctx) => {
    if (condition.criteriaType === "tag" && !(TAG_OPERATORS as readonly string[]).includes(condition.operator)) {
      ctx.addIssue({
        code: "custom",
        path: ["operator"],
        message: "Tag conditions only support = and ≠",
      })
    }

    if (
      condition.criteriaType === "evidenceCount" &&
      condition.criteriaValue.trim() !== "" &&
      !Number.isInteger(Number(condition.criteriaValue))
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["criteriaValue"],
        message: "Evidence count value must be a whole number",
      })
    }
  })

const brandRuleLevelSchema = z.object({
  level: z.number().int().min(0).max(255),
  precedingLogicalJoin: z.enum([...LOGICAL_JOIN]),
  ruleLevelType: z.enum([...RULE_LEVEL_TYPE]),
  brandRuleLevelConditions: z.array(brandRuleLevelConditionSchema),
})

const brandRuleOutputSchema = z.object({
  type: z.enum([...OUTPUT_TYPE]),
  tag: z.string().nullable(),
  tagType: z.string().nullable(),
  property: z.string().nullable(),
  value: z.string().nullable(),
})

const precedenceSchema = z
  .string()
  .trim()
  .min(1, "Precedence is required")
  .transform((value, ctx) => {
    const num = Number(value)

    if (!Number.isInteger(num)) {
      ctx.addIssue({ code: "custom", message: "Precedence must be a whole number" })
      return z.NEVER
    }

    if (num < 0 || num > 255) {
      ctx.addIssue({ code: "custom", message: "Precedence must be between 0 and 255" })
      return z.NEVER
    }

    return num
  })

const saveRuleFormSchema = z.object({
  name: nonEmptyString("Name is required"),
  ruleScope: nonEmptyString("Rule Scope is required").pipe(z.enum([...RULE_SCOPE])),
  precedence: precedenceSchema,
  output: brandRuleOutputSchema,
  brandRuleLevels: z.array(brandRuleLevelSchema),
})

const deleteRuleFormSchema = z.object({
  name: nonEmptyString("Name is required"),
})

const editRuleFormSchema = saveRuleFormSchema.extend({
  originalName: z.string().min(1),
})

async function action({ request }: ActionFunctionArgs) {
  try {
    const formData = await request.formData()
    const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

    switch (action) {
      case "SAVE_RULE": {
        let rawBrandRuleLevels: unknown
        try {
          rawBrandRuleLevels = JSON.parse((formData.get("brandRuleLevels") as string) || "[]")
        } catch {
          return { action, errorMessage: "Invalid rule levels" }
        }

        const parsed = saveRuleFormSchema.safeParse({
          name: formData.get("name"),
          ruleScope: formData.get("ruleScope"),
          precedence: formData.get("precedence"),
          output: {
            type: formData.get("outputType"),
            tag: (formData.get("outputTag") as string) || null,
            tagType: (formData.get("outputTagType") as string) || null,
            property: (formData.get("outputProperty") as string) || null,
            value: (formData.get("outputValue") as string) || null,
          },
          brandRuleLevels: rawBrandRuleLevels,
        })

        if (!parsed.success) {
          return { action, errorMessage: firstZodMessage(parsed.error, "Invalid rule") }
        }

        const payload: BrandRule = {
          brand: project,
          name: parsed.data.name,
          isDeleted: false,
          precedence: parsed.data.precedence,
          ruleScope: parsed.data.ruleScope,
          output: parsed.data.output,
          brandRuleLevels: parsed.data.brandRuleLevels,
        }

        try {
          const response = await fetchAPI(
            `/api/UnityViewer/Rule/${encodeURIComponent(organisation)}/${encodeURIComponent(project)}`,
            {
              method: "POST",
              body: payload as unknown as Record<string, unknown>,
            },
          )

          if (response.status === 304) {
            return {
              action,
              errorMessage: "A rule with this name already exists. Please use a different name.",
            }
          }

          if (!response.ok) {
            return { action, errorMessage: await extractErrorMessage(response, "Error saving rule") }
          }
          return { action }
        } catch {
          return { action, errorMessage: "Error saving rule" }
        }
      }
      case "EDIT_RULE": {
        let rawBrandRuleLevels: unknown
        try {
          rawBrandRuleLevels = JSON.parse((formData.get("brandRuleLevels") as string) || "[]")
        } catch {
          return { action, errorMessage: "Invalid rule levels" }
        }

        const parsed = editRuleFormSchema.safeParse({
          originalName: formData.get("originalName"),
          name: formData.get("name"),
          ruleScope: formData.get("ruleScope"),
          precedence: formData.get("precedence"),
          output: {
            type: formData.get("outputType"),
            tag: (formData.get("outputTag") as string) || null,
            tagType: (formData.get("outputTagType") as string) || null,
            property: (formData.get("outputProperty") as string) || null,
            value: (formData.get("outputValue") as string) || null,
          },
          brandRuleLevels: rawBrandRuleLevels,
        })

        if (!parsed.success) {
          return { action, errorMessage: firstZodMessage(parsed.error, "Invalid rule") }
        }

        const payload: BrandRule = {
          brand: project,
          name: parsed.data.name,
          isDeleted: false,
          precedence: parsed.data.precedence,
          ruleScope: parsed.data.ruleScope,
          output: parsed.data.output,
          brandRuleLevels: parsed.data.brandRuleLevels,
        }

        try {
          const response = await fetchAPI(
            `/api/UnityViewer/Rule/${encodeURIComponent(organisation)}/${encodeURIComponent(project)}/${encodeURIComponent(parsed.data.originalName)}`,
            {
              method: "PUT",
              body: payload as unknown as Record<string, unknown>,
            },
          )

          if (response.status === 304) {
            return {
              action,
              errorMessage: "A rule with this name already exists. Please use a different name.",
            }
          }

          if (!response.ok) {
            return { action, errorMessage: await extractErrorMessage(response, "Error updating rule") }
          }
          return { action }
        } catch {
          return { action, errorMessage: "Error updating rule" }
        }
      }

      case "DELETE_RULE": {
        const parsed = deleteRuleFormSchema.safeParse({ name: formData.get("name") })
        if (!parsed.success) {
          return { action, errorMessage: firstZodMessage(parsed.error, "Name is required") }
        }
        const { name } = parsed.data

        try {
          const response = await fetchAPI(
            `/api/UnityViewer/Rule/${encodeURIComponent(organisation)}/${encodeURIComponent(project)}/${encodeURIComponent(name)}`,
            {
              method: "DELETE",
            },
          )
          if (!response.ok) {
            return { action, errorMessage: await extractErrorMessage(response, "Error deleting rule") }
          }
          return { action }
        } catch {
          return { action, errorMessage: "Error deleting rule" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}
export function BrandRules() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRulesData()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData: FormData, reason: { type: "field"; name: string } | { type: "submitButton" }) => {
      if (reason.type !== "field") return
      if (reason.name === "organisation") {
        formData.delete("project")
      }
    },
    [],
  )

  return (
    <Fragment>
      {rootData != null && data != null && (
        <div className="flex items-center gap-4">
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            {rootData.organisations.length > 1 && (
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={data.loadedOrganisation}
                items={rootData.organisations}
              />
            )}
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
          </FiltersWrapper>
          <div className="flex-1" />
          <RuleAddNew />
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

function RuleRow({ rule, index }: { rule: BrandRule; index: number }) {
  return (
    <TableRow className="text-sm">
      <TableCell>{index + 1}.</TableCell>
      <TableCell className="font-medium">{rule.name}</TableCell>
      <TableCell>{RULE_SCOPE_LABELS[rule.ruleScope]}</TableCell>
      <TableCell>{rule.precedence}</TableCell>
      <TableCell>
        {rule.output.type === "tag" ? `Tag: ${rule.output.tag ?? "—"}` : `Metadata: ${rule.output.property ?? "—"}`}
      </TableCell>
      <TableCell>{rule.brandRuleLevels.length}</TableCell>
      <TableCell className="space-x-2">
        <RuleEdit rule={rule} />
        <RuleDelete rule={rule} />
      </TableCell>
    </TableRow>
  )
}

function Main() {
  const data = useRulesData()

  const tableContent = useMemo(() => {
    if (data?.rules == null) return

    if (data.rules.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} className="py-8 text-center text-gray-500">
            No rules found.
          </TableCell>
        </TableRow>
      )
    }

    return data.rules.map((rule: BrandRule, index: number) => (
      <RuleRow key={`rule-${rule.name}-${index}`} rule={rule} index={index} />
    ))
  }, [data?.rules])

  if (tableContent == null) {
    return <div className="py-8 text-center text-gray-500">Loading...</div>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Name</TableHead>
          <TableHead className="text-left">Scope</TableHead>
          <TableHead className="text-left">Precedence</TableHead>
          <TableHead className="text-left">Output</TableHead>
          <TableHead className="text-left">Levels</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

type LevelsUpdater = BrandRuleLevel[] | ((prev: BrandRuleLevel[]) => BrandRuleLevel[])

function TagTypeSelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const data = useRulesData()

  const tagTypes = useMemo(() => [...new Set((data?.tags ?? []).map((t: BrandTag) => t.tagType))], [data?.tags])

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select tag type" />
      </SelectTrigger>
      <SelectContent>
        {tagTypes.map((tagType) => (
          <SelectItem key={tagType} value={tagType}>
            {tagType}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TagValueSelect({
  tagType,
  value,
  onChange,
}: {
  tagType: string
  value: string
  onChange: (value: string) => void
}) {
  const data = useRulesData()

  const tagOptions = useMemo(
    () => [...new Set((data?.tags ?? []).filter((t: BrandTag) => t.tagType === tagType).map((t: BrandTag) => t.name))],
    [data?.tags, tagType],
  )

  return (
    <Select value={value} onValueChange={onChange} disabled={tagType === ""}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select tag" />
      </SelectTrigger>
      <SelectContent>
        {tagOptions.map((name) => (
          <SelectItem key={name} value={name}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function EvidenceCountLookupCombobox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const data = useRulesData()
  const records = data?.evidenceCounts ?? []
  const selected = records.find((r) => r.name === value)

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className="truncate">
            {selected != null ? selected.name : value !== "" ? value : "Select evidence count"}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="max-h-[min(16rem,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1"
      >
        {records.length === 0 && <p className="p-2 text-sm text-gray-500">No records found.</p>}
        {records.map((record) => (
          <PopoverClose asChild key={record.name}>
            <button
              type="button"
              onClick={() => onChange(record.name)}
              className="flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-gray-100"
            >
              <span className="flex min-w-0 flex-1 flex-col items-start">
                <span className="font-medium break-words">{record.name}</span>
                <span className="text-xs break-words text-gray-500">
                  Scope: {record.ruleScope} · Label: {record.criteriaLabel} · Type: {record.criteriaType}
                </span>
              </span>
              {record.name === value && <CheckIcon className="mt-0.5 size-4 shrink-0" />}
            </button>
          </PopoverClose>
        ))}
      </PopoverContent>
    </Popover>
  )
}

function ConditionLabelField({
  criteriaType,
  value,
  onChange,
}: {
  criteriaType: (typeof CRITERIA_TYPE)[number]
  value: string
  onChange: (value: string) => void
}) {
  if (criteriaType === "evidenceCount") {
    return <EvidenceCountLookupCombobox value={value} onChange={onChange} />
  }

  return <TagTypeSelect value={value} onChange={onChange} />
}

function ConditionValueField({
  criteriaType,
  criteriaLabel,
  value,
  onChange,
}: {
  criteriaType: (typeof CRITERIA_TYPE)[number]
  criteriaLabel: string
  value: string
  onChange: (value: string) => void
}) {
  if (criteriaType === "tag") {
    return <TagValueSelect tagType={criteriaLabel} value={value} onChange={onChange} />
  }

  return (
    <Input
      type="number"
      step={1}
      placeholder="Value"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full focus-visible:ring-0 focus-visible:ring-offset-0"
    />
  )
}

function RuleLevelBuilder({
  levels,
  onChange,
}: {
  levels: BrandRuleLevel[]
  onChange: (updater: LevelsUpdater) => void
}) {
  const addLevel = useCallback(() => {
    onChange((prev) => [
      ...prev,
      { ...emptyLevel(prev.length), precedingLogicalJoin: prev.length === 0 ? "none" : "and" },
    ])
  }, [onChange])

  const removeLevel = useCallback(
    (levelIndex: number) => {
      onChange((prev) =>
        prev
          .filter((_, i) => i !== levelIndex)
          .map((l, i) => ({
            ...l,
            level: i,
            precedingLogicalJoin: i === 0 ? "none" : l.precedingLogicalJoin === "none" ? "and" : l.precedingLogicalJoin,
          })),
      )
    },
    [onChange],
  )

  const updateLevel = useCallback(
    (levelIndex: number, patch: Partial<BrandRuleLevel>) => {
      onChange((prev) => prev.map((l, i) => (i === levelIndex ? { ...l, ...patch } : l)))
    },
    [onChange],
  )

  const addCondition = useCallback(
    (levelIndex: number) => {
      onChange((prev) =>
        prev.map((l, i) =>
          i === levelIndex
            ? {
                ...l,
                brandRuleLevelConditions: [
                  ...l.brandRuleLevelConditions,
                  {
                    ...emptyCondition(),
                    precedingLogicalJoin: l.brandRuleLevelConditions.length === 0 ? "none" : "and",
                  },
                ],
              }
            : l,
        ),
      )
    },
    [onChange],
  )

  const removeCondition = useCallback(
    (levelIndex: number, conditionIndex: number) => {
      onChange((prev) =>
        prev.map((l, i) =>
          i === levelIndex
            ? {
                ...l,
                brandRuleLevelConditions: l.brandRuleLevelConditions
                  .filter((_, ci) => ci !== conditionIndex)
                  .map((c, ci) => ({
                    ...c,
                    precedingLogicalJoin:
                      ci === 0 ? "none" : c.precedingLogicalJoin === "none" ? "and" : c.precedingLogicalJoin,
                  })),
              }
            : l,
        ),
      )
    },
    [onChange],
  )

  const updateCondition = useCallback(
    (levelIndex: number, conditionIndex: number, patch: Partial<BrandRuleLevelCondition>) => {
      onChange((prev) =>
        prev.map((l, i) =>
          i === levelIndex
            ? {
                ...l,
                brandRuleLevelConditions: l.brandRuleLevelConditions.map((c, ci) =>
                  ci === conditionIndex ? { ...c, ...patch } : c,
                ),
              }
            : l,
        ),
      )
    },
    [onChange],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Rule Levels</label>
        <Button type="button" variant="outline" size="sm" onClick={addLevel}>
          <PlusIcon className="size-4" /> Add Level
        </Button>
      </div>

      {levels.length === 0 && <p className="text-sm text-gray-500">No levels added yet.</p>}

      {levels.map((level, levelIndex) => (
        <div key={`level-${levelIndex}`} className="space-y-3 rounded-md border border-gray-200 p-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Level {level.level}</span>

            {levelIndex > 0 && (
              <Select
                value={level.precedingLogicalJoin}
                onValueChange={(v) =>
                  updateLevel(levelIndex, { precedingLogicalJoin: v as (typeof LOGICAL_JOIN)[number] })
                }
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOGICAL_JOIN.filter((j) => j !== "none").map((join) => (
                    <SelectItem key={join} value={join}>
                      {LOGICAL_JOIN_LABELS[join]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={level.ruleLevelType}
              onValueChange={(v) => updateLevel(levelIndex, { ruleLevelType: v as (typeof RULE_LEVEL_TYPE)[number] })}
            >
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RULE_LEVEL_TYPE.map((t) => (
                  <SelectItem key={t} value={t}>
                    {RULE_LEVEL_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex-1" />

            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove Level"
              onClick={() => removeLevel(levelIndex)}
            >
              <TrashIcon className="text-red-500" />
            </Button>
          </div>

          <div className="space-y-2 pl-4">
            {level.brandRuleLevelConditions.map((condition, conditionIndex) => (
              <div
                key={`condition-${levelIndex}-${conditionIndex}`}
                className="space-y-2 rounded-md border border-gray-100 p-2"
              >
                {conditionIndex > 0 && (
                  <Select
                    value={condition.precedingLogicalJoin}
                    onValueChange={(v) =>
                      updateCondition(levelIndex, conditionIndex, {
                        precedingLogicalJoin: v as (typeof LOGICAL_JOIN)[number],
                      })
                    }
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOGICAL_JOIN.filter((j) => j !== "none").map((join) => (
                        <SelectItem key={join} value={join}>
                          {LOGICAL_JOIN_LABELS[join]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <div className="grid grid-cols-[150px_minmax(0,1fr)_auto] items-center gap-2">
                  <Select
                    value={condition.criteriaType}
                    onValueChange={(v) =>
                      updateCondition(levelIndex, conditionIndex, {
                        criteriaType: v as (typeof CRITERIA_TYPE)[number],
                        criteriaValue: "",
                        criteriaLabel: "",
                        operator: "=",
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CRITERIA_TYPE.map((c) => (
                        <SelectItem key={c} value={c}>
                          {CRITERIA_TYPE_LABELS[c]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ConditionLabelField
                    criteriaType={condition.criteriaType}
                    value={condition.criteriaLabel}
                    onChange={(v) =>
                      updateCondition(
                        levelIndex,
                        conditionIndex,
                        condition.criteriaType === "tag"
                          ? { criteriaLabel: v, criteriaValue: "" }
                          : { criteriaLabel: v },
                      )
                    }
                  />
                  <div />
                  <Select
                    value={condition.operator}
                    onValueChange={(v) =>
                      updateCondition(levelIndex, conditionIndex, { operator: v as (typeof OPERATOR)[number] })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operatorsFor(condition.criteriaType).map((op) => (
                        <SelectItem key={op} value={op}>
                          {OPERATOR_LABELS[op]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ConditionValueField
                    criteriaType={condition.criteriaType}
                    criteriaLabel={condition.criteriaLabel}
                    value={condition.criteriaValue}
                    onChange={(v) => updateCondition(levelIndex, conditionIndex, { criteriaValue: v })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove Condition"
                    onClick={() => removeCondition(levelIndex, conditionIndex)}
                  >
                    <TrashIcon className="size-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" size="sm" onClick={() => addCondition(levelIndex)}>
              <PlusIcon className="size-4" /> Add Condition
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}

function RuleAddNew() {
  const [open, setOpen] = useState(false)

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Rule</DialogTitle>
        </DialogHeader>
        <RuleForm closeDialog={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

function RuleEdit({ rule }: { rule: BrandRule }) {
  const [open, setOpen] = useState(false)

  if (!hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Edit Rule">
          <PencilIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Rule</DialogTitle>
        </DialogHeader>
        <RuleForm closeDialog={() => setOpen(false)} rule={rule} />
      </DialogContent>
    </Dialog>
  )
}

type FormScalarField =
  | "name"
  | "ruleScope"
  | "precedence"
  | "outputType"
  | "outputTag"
  | "outputTagType"
  | "outputProperty"
  | "outputValue"

interface RuleFormState {
  name: string
  ruleScope: string
  precedence: string
  outputType: (typeof OUTPUT_TYPE)[number]
  outputTag: string
  outputTagType: string
  outputProperty: string
  outputValue: string
  levels: BrandRuleLevel[]
}

type RuleFormAction =
  | { type: "SET_FIELD"; field: FormScalarField; value: string }
  | { type: "SET_LEVELS"; updater: LevelsUpdater }

function initialRuleFormState(rule?: BrandRule): RuleFormState {
  if (rule == null) {
    return {
      name: "",
      ruleScope: "",
      precedence: "",
      outputType: "tag",
      outputTag: "",
      outputTagType: "",
      outputProperty: "",
      outputValue: "",
      levels: [],
    }
  }

  return {
    name: rule.name,
    ruleScope: rule.ruleScope,
    precedence: String(rule.precedence),
    outputType: rule.output.type,
    outputTag: rule.output.tag ?? "",
    outputTagType: rule.output.tagType ?? "",
    outputProperty: rule.output.property ?? "",
    outputValue: rule.output.value ?? "",
    levels: rule.brandRuleLevels.map((level, levelIndex) => ({
      ...level,
      precedingLogicalJoin: levelIndex === 0 ? "none" : level.precedingLogicalJoin,
      brandRuleLevelConditions: level.brandRuleLevelConditions.map((condition, conditionIndex) => ({
        ...condition,
        precedingLogicalJoin: conditionIndex === 0 ? "none" : condition.precedingLogicalJoin,
      })),
    })),
  }
}

function normalizeLevelsForSubmit(levels: BrandRuleLevel[]): BrandRuleLevel[] {
  return levels.map((level, levelIndex) => ({
    ...level,
    precedingLogicalJoin: levelIndex === 0 ? "none" : level.precedingLogicalJoin,
    brandRuleLevelConditions: level.brandRuleLevelConditions.map((condition, conditionIndex) => ({
      ...condition,
      precedingLogicalJoin: conditionIndex === 0 ? "none" : condition.precedingLogicalJoin,
    })),
  }))
}

function ruleFormReducer(state: RuleFormState, action: RuleFormAction): RuleFormState {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value }
    case "SET_LEVELS":
      return {
        ...state,
        levels: typeof action.updater === "function" ? action.updater(state.levels) : action.updater,
      }
    default:
      return state
  }
}

function TagCombobox({
  value,
  tagType,
  onSelect,
}: {
  value: string
  tagType: string
  onSelect: (tag: string, tagType: string) => void
}) {
  const data = useRulesData()
  const tags = data?.tags ?? []

  const tagTypes = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    for (const t of tags) {
      if (!seen.has(t.tagType)) {
        seen.add(t.tagType)
        list.push(t.tagType)
      }
    }
    return list
  }, [tags])

  const tagsForType = useMemo(() => tags.filter((t: BrandTag) => t.tagType === tagType), [tags, tagType])

  return (
    <>
      <div>
        <label className="text-sm font-medium">Tag Type</label>
        <Select
          value={tagType}
          onValueChange={(newTagType) => {
            onSelect("", newTagType)
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select tag type" />
          </SelectTrigger>
          <SelectContent>
            {tagTypes.map((tt) => (
              <SelectItem key={tt} value={tt}>
                {tt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Tag</label>
        <Select value={value} onValueChange={(newTag) => onSelect(newTag, tagType)} disabled={tagType === ""}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select tag" />
          </SelectTrigger>
          <SelectContent>
            {tagsForType.map((t: BrandTag) => (
              <SelectItem key={t.name} value={t.name}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

function RuleForm({ closeDialog, rule }: { closeDialog: () => void; rule?: BrandRule }) {
  const fetcher = useFetcher<typeof action>()
  const data = useRulesData()!
  const mode = rule != null ? "edit" : "create"

  const [state, dispatch] = useReducer(ruleFormReducer, rule, initialRuleFormState)

  const setField = useCallback(
    (field: FormScalarField) => (value: string) => dispatch({ type: "SET_FIELD", field, value }),
    [],
  )
  const setLevels = useCallback((updater: LevelsUpdater) => dispatch({ type: "SET_LEVELS", updater }), [])

  useEffect(() => {
    if (
      fetcher.state === "idle" &&
      (fetcher.data?.action === "SAVE_RULE" || fetcher.data?.action === "EDIT_RULE") &&
      fetcher.data?.errorMessage == null
    ) {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, closeDialog])

  return (
    <fetcher.Form method="POST" className="flex max-h-[70vh] min-w-0 flex-col">
      <input type="hidden" name="action" value={mode === "edit" ? "EDIT_RULE" : "SAVE_RULE"} />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="brandRuleLevels" value={JSON.stringify(normalizeLevelsForSubmit(state.levels))} />
      {mode === "edit" && rule != null && <input type="hidden" name="originalName" value={rule.name} />}

      <div className="flex-1 space-y-4 overflow-y-auto pr-1">
        <div>
          <label className="text-sm font-medium">Name</label>
          <Input
            name="name"
            type="text"
            placeholder="Enter rule name"
            required
            autoFocus
            value={state.name}
            onChange={(e) => setField("name")(e.target.value)}
            className="focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Rule Scope</label>
            <Select value={state.ruleScope} onValueChange={setField("ruleScope")}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent>
                {RULE_SCOPE.map((scope) => (
                  <SelectItem key={scope} value={scope}>
                    {RULE_SCOPE_LABELS[scope]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="ruleScope" value={state.ruleScope} />
          </div>

          <div>
            <label className="text-sm font-medium">Precedence</label>
            <Input
              name="precedence"
              type="number"
              min={0}
              max={255}
              value={state.precedence}
              onChange={(e) => setField("precedence")(e.target.value)}
              required
              className="focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
        </div>

        <div className="space-y-3 rounded-md border border-gray-200 p-3">
          <label className="text-sm font-medium">Output</label>

          <Select value={state.outputType} onValueChange={(v) => setField("outputType")(v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTPUT_TYPE.map((type) => (
                <SelectItem key={type} value={type}>
                  {OUTPUT_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <input type="hidden" name="outputType" value={state.outputType} />
          {state.outputType === "tag" ? (
            <div className="grid grid-cols-2 gap-3">
              <TagCombobox
                value={state.outputTag}
                tagType={state.outputTagType}
                onSelect={(tag, tagType) => {
                  dispatch({ type: "SET_FIELD", field: "outputTag", value: tag })
                  dispatch({ type: "SET_FIELD", field: "outputTagType", value: tagType })
                }}
              />
              <input type="hidden" name="outputTag" value={state.outputTag} />
              <input type="hidden" name="outputTagType" value={state.outputTagType} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Input
                name="outputProperty"
                placeholder="Property"
                value={state.outputProperty}
                onChange={(e) => setField("outputProperty")(e.target.value)}
                className="focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Input
                name="outputValue"
                placeholder="Value"
                value={state.outputValue}
                onChange={(e) => setField("outputValue")(e.target.value)}
                className="focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          )}
        </div>

        <RuleLevelBuilder levels={state.levels} onChange={setLevels} />
      </div>

      <div className="mt-4 flex items-center justify-end gap-4 border-t border-gray-200 pt-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Saving...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={fetcher.state !== "idle"}>
          {mode === "edit" ? "Save Changes" : "Save Rule"}
        </Button>
      </div>
    </fetcher.Form>
  )
}

function RuleDelete({ rule }: { rule: BrandRule }) {
  if (!hasPermission("deleteAccess")) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Rule">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm text-pretty">
          Delete rule <span className="font-semibold">&quot;{rule.name}&quot;</span>?
        </p>
        <RuleDeleteForm rule={rule} />
      </PopoverContent>
    </Popover>
  )
}

function RuleDeleteForm({ rule }: { rule: BrandRule }) {
  const fetcher = useFetcher<typeof action>()
  const data = useRulesData()!

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE_RULE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={rule.name} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        fetcher.data?.action === "DELETE_RULE" &&
        fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button type="button">Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost" disabled={fetcher.state !== "idle"}>
        Confirm
      </Button>
    </fetcher.Form>
  )
}

BrandRules.loader = loader
BrandRules.Main = Main
BrandRules.action = action
BrandRules.routeId = routeId
